import React, { useMemo } from 'react';
import { DocumentTextIcon } from '@heroicons/react/solid';
import {
  FidelityContractData,
  extractApprovedYears,
  extractContractYears,
  extractPendingYears,
  isAfterRenewalDate,
} from '../../libs/fidelityContract';

interface FidelityContractSectionProps {
  fidelity?: FidelityContractData | null;
  isCreateMode?: boolean;
}

const FidelityContractSection: React.FC<FidelityContractSectionProps> = ({
  fidelity,
  isCreateMode,
}) => {
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  const afterRenewal = isAfterRenewalDate();
  const approvedYears = useMemo(
    () =>
      extractApprovedYears(fidelity?.contratto, fidelity?.annoUltimaApprovazione),
    [fidelity?.contratto, fidelity?.annoUltimaApprovazione]
  );
  const pendingYears = useMemo(
    () =>
      extractPendingYears(fidelity?.contratto, fidelity?.annoUltimaApprovazione),
    [fidelity?.contratto, fidelity?.annoUltimaApprovazione]
  );
  const existingYears = useMemo(
    () => extractContractYears(fidelity?.contratto),
    [fidelity?.contratto]
  );
  const visibleYears = useMemo(
    () => (afterRenewal ? [currentYear, nextYear] : [currentYear]),
    [afterRenewal, currentYear, nextYear]
  );
  const lastActiveVisibleYear = useMemo(() => {
    const activeVisibleYears = visibleYears.filter((year) =>
      approvedYears.includes(year)
    );
    if (activeVisibleYears.length === 0) return null;
    return Math.max(...activeVisibleYears);
  }, [approvedYears, visibleYears]);
  const yearsData = fidelity?.contratto?.years || {};

  if (!fidelity && !isCreateMode) return null;

  const getYearStateLabel = (year: number) => {
    if (approvedYears.includes(year)) return 'Attivo';
    if (pendingYears.includes(year)) return 'In approvazione';
    if (existingYears.includes(year)) return 'Firmato';
    return 'Non firmato';
  };

  const getYearStateClassName = (year: number) => {
    if (approvedYears.includes(year)) return 'bg-green-100 text-green-800';
    if (pendingYears.includes(year)) return 'bg-yellow-100 text-yellow-800';
    if (existingYears.includes(year)) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className='bg-white shadow overflow-hidden sm:rounded-lg mb-6'>
      <div className='px-4 py-5 sm:px-6'>
        <div>
          <div className='flex items-center'>
            <DocumentTextIcon className='h-5 w-5 text-primary mr-2' />
            <h3 className='text-lg leading-6 font-medium text-gray-900'>
              Contratto
            </h3>
          </div>
        </div>
      </div>

      {isCreateMode ? (
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
          <div className='bg-blue-50 border border-blue-200 rounded-md p-4'>
            <h5 className='text-sm font-medium text-blue-900'>
              Gestione contratto disponibile dopo il salvataggio
            </h5>
            <p className='mt-1 text-sm text-blue-700'>
              Crea prima la fidelity e poi modifica lo stato contratto da questa
              sezione.
            </p>
          </div>
        </div>
      ) : (
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6 space-y-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {visibleYears.map((year) => (
              <div
                key={year}
                className='border border-gray-200 rounded-lg p-4 space-y-2'
              >
                <div className='text-sm font-medium text-gray-900 mb-2'>{year}</div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getYearStateClassName(
                    year
                  )}`}
                >
                  {getYearStateLabel(year)}
                </span>
                {hasSignedAt(yearsData[String(year)]?.signedAt) && (
                  <div className='text-sm text-gray-600'>
                    Firmato il:{' '}
                    <span className='font-medium text-gray-900'>
                      {formatSignedAt(yearsData[String(year)]?.signedAt)}
                    </span>
                  </div>
                )}
                {year === lastActiveVisibleYear &&
                  hasExpiryDate(fidelity?.scadenzaContratto) && (
                    <div className='text-sm text-gray-600'>
                      Scadenza contratto:{' '}
                      <span className='font-medium text-gray-900'>
                        {formatContractExpiryDate(fidelity?.scadenzaContratto)}
                      </span>
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const formatContractExpiryDate = (dateValue?: string | null) => {
  if (!dateValue) return '-';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('it-IT');
};

const hasExpiryDate = (dateValue?: string | null) => {
  if (!dateValue) return false;
  const date = new Date(dateValue);
  return !Number.isNaN(date.getTime());
};

const formatSignedAt = (dateValue?: string) => {
  if (!dateValue) return '-';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('it-IT');
};

const hasSignedAt = (dateValue?: string) => {
  if (!dateValue) return false;
  const date = new Date(dateValue);
  return !Number.isNaN(date.getTime());
};

export default FidelityContractSection;
